<template>
  <article>
    <p class="margin-bottom-2xl">
      Font Awesome comes with two different web frameworks for easily using icons on the web: our original
      <strong>Web Fonts &amp; CSS</strong>
      method and our newer
      <strong>SVG &amp; JS</strong>
      framework. Each has benefits and caveats - pick the one that works best for your project and situation.
    </p>

    <div style="overflow: scroll">
      <table class="roomy">
        <thead>
          <tr>
            <th><span class="sr-only">Feature/Option</span></th>
            <th>Web Fonts &amp; CSS</th>
            <th>SVG &amp; JS</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">How Icons Render</th>
            <td>
              As CSS pseudo-elements and styled as the Font Awesome
              <code class="orange5">font-family</code>.
            </td>
            <td>As SVG elements in your page's HTML.</td>
          </tr>
          <tr>
            <th scope="row">Best For</th>
            <td>
              <p>People who are used to Font Awesome's older versions.</p>
              <p>People who projects already using Font Awesome 4.X.</p>
              <p>People using content management systems and site building tools like WordPress and Squarespace.</p>
            </td>
            <td>
              <p>People who prefer to use SVGs to display icons.</p>
              <p>People who don't mind rolling up their sleeves with Front End code.</p>
              <p>People who want Auto-Accessibility enabled for their icons.</p>
            </td>
          </tr>
          <tr>
            <th scope="row">Upgrading Difficulty</th>
            <td>Similar file structure and framework. Shim available.</td>
            <td>Different files and references needed. Some rendering proofing is needed. Shim available.</td>
          </tr>
          <tr>
            <th scope="row">Requirements</th>
            <td>CSS and a browser that supports web fonts (leveraging <code class="orange5">@font-face</code>).</td>
            <td>CSS, JavaScript, and a browser that supports rendering SVGs.</td>
          </tr>
          <tr>
            <th scope="row">CSS-only Kits</th>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
            <td>
              <span class="sr-only">Not supported</span>
            </td>
          </tr>
          <tr>
            <th scope="row">Icon Sizing</th>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
          </tr>
          <tr>
            <th scope="row">Icon Rotating</th>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
          </tr>
          <tr>
            <th scope="row">Listed, Bordered, and Pulled Styling</th>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
          </tr>
          <tr>
            <th scope="row">Icon Animations</th>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
          </tr>
          <tr>
            <th scope="row">Stacking Icons</th>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
          </tr>
          <tr>
            <th scope="row">Power Transforms</th>
            <td>
              <span class="sr-only">Not supported</span>
            </td>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
          </tr>
          <tr>
            <th scope="row">Masking</th>
            <td>
              <span class="sr-only">Not supported</span>
            </td>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
          </tr>
          <tr>
            <th scope="row">Layering Text and Counters</th>
            <td>
              <span class="sr-only">Not supported</span>
            </td>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
          </tr>
          <tr>
            <th scope="row">Auto-Accessibility</th>
            <td>
              <span class="sr-only">Not supported</span>
            </td>
            <td>
              <font-awesome-icon
                :icon="$v7.solid.faCheckCircle"
                size="lg"
                style="color: var(--fa-teal)"
              />
              <span class="sr-only">Supported</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </article>
</template>
